/**
 * The report a parent sees at the end of the Free PEP Readiness Check.
 *
 * Everything on this page is measured, not asserted. The national position only appears when the
 * server had enough norm coverage to compute one, and the offer block is the last thing on the
 * page because the value has to land first.
 */
import { Button } from "@/components/ui/button";
import { Illustration } from "@/components/Illustration";
import { subjectStyle, bandStyle } from "./subjects";
import {
  ArrowRight, CalendarDays, CheckCircle2, ClipboardCheck, Flag, Mail,
  Target, Timer, TrendingUp, Users,
} from "lucide-react";

export interface PepSubjectScore { subject: string; correct: number; total: number; percent: number }
export interface PepFocusTopic { subject: string; topic: string; correct: number; total: number; description: string }
export interface PepOffer {
  code: string; url: string; headline: string; detail: string; kind: string; value: number | null;
  expiresAt: string | null; deadlineLabel: string | null; origin: string;
}
export interface PepCountdownData {
  days: number; date: string; dateLabel: string; subjects: string[]; isTheirSitting: boolean; headline: string;
}

export interface PepReport {
  id: string;
  grade: number;
  firstName: string | null;
  captured: boolean;
  emailed: boolean;
  score: number;
  total: number;
  percent: number;
  band: string;
  percentile: number | null;
  percentileBasis: string | null;
  subjects: PepSubjectScore[];
  strongestSubject: string | null;
  weakestSubject: string | null;
  weakestTopic: string | null;
  focusTopics: PepFocusTopic[];
  paper: { subject: string; whatItAsks: string; sittingLabel: string | null } | null;
  countdown: PepCountdownData | null;
  offer: PepOffer;
}

const BAND_LINE: Record<string, string> = {
  "Highly Proficient": "That is the top band. The job now is keeping it there until March.",
  "Proficient": "That is a solid pass band. A few fixed gaps would push it to the top band.",
  "Developing": "That is below the pass band, and it is very fixable with regular practice.",
  "Beginning": "That is well below the pass band. There is time, but the work has to start now.",
};

function Section({ title, icon: Icon, children }: { title: string; icon: any; children: React.ReactNode }) {
  return (
    <section className="rounded-xl border border-border bg-card p-4 md:p-5 shadow-sm">
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4 text-primary" />
        <h2 className="text-base md:text-lg font-semibold">{title}</h2>
      </div>
      {children}
    </section>
  );
}

export function PepResult({ report, onOfferClick }: { report: PepReport; onOfferClick: (where: string) => void }) {
  const band = bandStyle(report.band);
  const emailedName = report.firstName ? `, ${report.firstName}` : "";

  return (
    <div className="space-y-4">
      {/* Score */}
      <section className="rounded-xl border border-border bg-card p-5 md:p-6 shadow-sm" data-testid="pep-score">
        <div className="flex items-center gap-4">
          <Illustration name={report.percent >= 65 ? "owl-cheer" : "owl-chart"} size={84} className="flex-shrink-0" />
          <div className="min-w-0">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">PEP Readiness Check</p>
            <p className="text-4xl md:text-5xl font-bold tracking-tight tabular-nums">
              {report.score}<span className="text-2xl md:text-3xl text-muted-foreground"> / {report.total}</span>
            </p>
            <span className={`inline-block mt-2 text-sm font-semibold px-3 py-1 rounded-full ${band.chip}`} data-testid="pep-band">
              {report.band}
            </span>
          </div>
        </div>
        <div className={`h-2 rounded-full mt-4 ${band.track}`}>
          <div className={`h-2 rounded-full ${band.bar}`} style={{ width: `${Math.max(4, report.percent)}%` }} />
        </div>
        <p className="text-sm text-muted-foreground mt-3 leading-relaxed">{BAND_LINE[report.band]}</p>
      </section>

      {/* National position, only when the norms actually support it */}
      {report.percentile !== null && (
        <section className="rounded-xl border border-primary/25 bg-primary/10 p-4 md:p-5" data-testid="pep-percentile">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-xl bg-primary text-primary-foreground flex items-center justify-center flex-shrink-0 rotate-2">
              <Users className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <p className="text-lg font-semibold leading-snug">
                Ahead of about <span className="tabular-nums">{report.percentile}%</span> of Grade {report.grade} students
              </p>
              {report.percentileBasis && (
                <p className="text-sm text-muted-foreground mt-1">
                  Measured against {report.percentileBasis} on the same topics.
                </p>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Subject breakdown */}
      <Section title="Subject by subject" icon={TrendingUp}>
        <div className="space-y-3">
          {report.subjects.map((s) => {
            const style = subjectStyle(s.subject);
            const Icon = style.icon;
            return (
              <div key={s.subject} className={`rounded-xl border p-3 ${style.card}`} data-testid={`pep-subject-${s.subject.toLowerCase().replace(/\s+/g, "-")}`}>
                <div className="flex items-center gap-3">
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 -rotate-2 ${style.sticker}`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline justify-between gap-2">
                      <p className="font-semibold truncate">{s.subject}</p>
                      <p className="text-sm font-semibold tabular-nums flex-shrink-0">{s.correct} / {s.total}</p>
                    </div>
                    <div className={`h-2 rounded-full mt-2 ${style.track}`}>
                      <div className={`h-2 rounded-full ${style.bar}`} style={{ width: `${Math.max(4, s.percent)}%` }} />
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        {report.strongestSubject && report.weakestSubject && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
            <div className="rounded-xl border border-border p-3">
              <p className="text-xs font-medium text-muted-foreground">Strongest</p>
              <p className="font-semibold flex items-center gap-1.5 mt-0.5">
                <CheckCircle2 className="w-4 h-4 text-emerald-600" />{report.strongestSubject}
              </p>
            </div>
            <div className="rounded-xl border border-border p-3">
              <p className="text-xs font-medium text-muted-foreground">Needs work first</p>
              <p className="font-semibold flex items-center gap-1.5 mt-0.5">
                <Flag className="w-4 h-4 text-amber-600" />{report.weakestSubject}
              </p>
            </div>
          </div>
        )}
      </Section>

      {/* The two topics to work on */}
      {report.focusTopics.length > 0 && (
        <Section title="Two things to work on this week" icon={Target}>
          <ol className="space-y-3">
            {report.focusTopics.map((t, i) => {
              const style = subjectStyle(t.subject);
              return (
                <li key={`${t.subject}-${t.topic}`} className="flex items-start gap-3" data-testid={`pep-focus-${i}`}>
                  <span className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 text-sm font-bold ${style.sticker}`}>
                    {i + 1}
                  </span>
                  <div className="min-w-0">
                    <p className="font-semibold">
                      {t.topic} <span className={`ml-1 text-xs font-semibold px-2 py-0.5 rounded-full ${style.chip}`}>{t.subject}</span>
                    </p>
                    <p className="text-sm text-muted-foreground mt-0.5">
                      {t.correct} of {t.total} right. {t.description}
                    </p>
                  </div>
                </li>
              );
            })}
          </ol>
        </Section>
      )}

      {/* What the paper asks for */}
      {report.paper && (
        <Section title={`What the ${report.paper.subject} paper actually asks for`} icon={ClipboardCheck}>
          <p className="text-sm leading-relaxed text-muted-foreground">{report.paper.whatItAsks}</p>
          {report.paper.sittingLabel && (
            <p className="text-sm font-medium mt-3">Sat on {report.paper.sittingLabel}.</p>
          )}
        </Section>
      )}

      {/* Countdown */}
      {report.countdown && (
        <div className="rounded-xl border border-border bg-card p-4 flex items-center gap-3 shadow-sm" data-testid="pep-countdown">
          <CalendarDays className="w-5 h-5 text-primary flex-shrink-0" />
          <p className="text-sm">
            <span className="font-semibold">{report.countdown.headline}.</span>{" "}
            <span className="text-muted-foreground">
              {report.countdown.subjects.join(" and ")} on {report.countdown.dateLabel}.
            </span>
          </p>
        </div>
      )}

      {/* The offer */}
      <section className="rounded-xl border-2 border-primary bg-primary/10 p-5 md:p-6" data-testid="pep-offer">
        <div className="flex items-start gap-4">
          <Illustration name="owl-rocket" size={72} className="hidden sm:block flex-shrink-0" />
          <div className="min-w-0">
            <h2 className="text-xl md:text-2xl font-bold tracking-tight">{report.offer.headline}</h2>
            <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{report.offer.detail}</p>
            <ul className="mt-4 space-y-2 text-sm">
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                <span>Practice that adapts to the exact topics this check flagged{report.weakestTopic ? `, starting with ${report.weakestTopic}` : ""}.</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                <span>Performance Tasks in the real PEP format, marked with the real rubric.</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                <span>A weekly progress report sent to you, so you always know where things stand.</span>
              </li>
            </ul>
            <Button
              size="lg"
              className="w-full sm:w-auto mt-5 h-12 text-base font-semibold"
              onClick={() => onOfferClick("offer")}
              data-testid="button-pep-offer"
            >
              {report.offer.headline}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <p className="text-xs text-muted-foreground mt-2">
              Code <span className="font-semibold tracking-wide">{report.offer.code}</span> is applied for you.
            </p>
            {report.offer.deadlineLabel && (
              <p className="text-sm font-semibold flex items-center gap-1.5 mt-3" data-testid="pep-offer-deadline">
                <Timer className="w-4 h-4 text-primary" />
                This link closes {report.offer.deadlineLabel}
              </p>
            )}
          </div>
        </div>
      </section>

      {/* Performance Task teaser */}
      <section className="rounded-xl border border-border bg-card p-4 md:p-5 shadow-sm" data-testid="pep-task-teaser">
        <div className="flex items-start gap-3">
          <Illustration name="owl-book" size={56} className="flex-shrink-0" />
          <div className="min-w-0">
            <h2 className="text-base md:text-lg font-semibold">See a real marked Performance Task</h2>
            <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
              The Performance Task is where most children lose marks, because it asks them to explain and justify, not
              just answer. Sign up and you can open one and read the marking, criterion by criterion.
            </p>
            <Button
              variant="outline"
              className="mt-3"
              onClick={() => onOfferClick("performance-task")}
              data-testid="button-pep-task-teaser"
            >
              Open a marked task
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {report.captured && (
        <p className="text-sm text-muted-foreground flex items-start gap-2 px-1" data-testid="pep-email-note">
          <Mail className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>
            {report.emailed
              ? `I sent this report to your inbox too${emailedName}. Check spam if it is not there in a few minutes.`
              : "This report is on screen for you now. If it does not reach your inbox, this page is the copy."}
          </span>
        </p>
      )}
    </div>
  );
}
